import {IPoll, IPollOptions} from "./types/poll.types";
import EmptyData from "../common/empty-data/empty-data";
import {ChartColumnBig, Check, Eye, Info, LockKeyhole, X} from "lucide-react";
import '../../styles/component/poll/poll.scss';
import LoadingSkeleton from "../common/loading-skeleton/loading-skeleton";
import {Button, message, Radio} from "antd";
import React, {useEffect, useState} from "react";
import server from "../../server";
import PollToolbar from "./utils/poll-toolbar";

interface PollViewProps {
    polls: IPoll[],
    loadingPolls: boolean,
    handleViewResult: (id: any) => void;
    handleOnClick: (id: any) => void;
    fetchPolls: () => void;
    onFilterChange: (type: string) => void;
    handleDateChange: (range: { startDate: string, endDate: string }) => void;
}

const PollEmployeeView = (props: PollViewProps) => {
    const [selectedVotes, setSelectedVotes] = useState<Record<string, string>>({});

    useEffect(() => {
        const votes: Record<string, string> = {};
        props.polls.forEach((poll) => {
            if (poll.votedOption) {
                votes[poll.id] = poll.votedOption;
            }
        });
        setSelectedVotes(votes);
    }, [props.polls]);

    async function handleVoteSubmit(pollId: string) {
        // Get the vote associated with this poll ID and store it in selectedVote
        const selectedVote = selectedVotes[pollId];

        if (!selectedVote) {
            message.warning("Please select an option");
            return;
        }

        try {
            await server.poll.vote(selectedVote);
            message.success("Vote submitted successfully!");
            await props.fetchPolls();
        } catch {
            message.error("Failed to submit vote.");
        }
    }

    return (
        <>
            <PollToolbar onFilterChange={props.onFilterChange} fetchPolls={props.fetchPolls}
                         handleDateChange={props.handleDateChange}/>
            <LoadingSkeleton loading={props.loadingPolls}>
                {props.polls.length > 0 ? (
                    <div className={'poll-container'}>
                        {props.polls.map((item: IPoll) => (
                            <div className={`poll ${item.anonymous ? "vote" : "poll"}`} key={item.id}>
                                <div className={'poll-status'}>
                                    <div className={`status ${item.anonymous ? "voting" : "polling"}`}>
                                        {item.anonymous ? "Vote" : "Poll"}
                                    </div>
                                    <div className={`status ${item.anonymous ? "anonymous" : "non-anonymous"}`}>
                                        {item.anonymous ? "Anonymous" : "Non-Anonymous"}
                                    </div>
                                    <div className={`status ${item.anonymous ? "results-after" : "results-live"}`}>
                                        {item.anonymous ? "Results After Close" : "Results Live"}
                                    </div>
                                    <div className={`status ${item.active ? "active" : "ended"}`}>
                                        {item.active ? "Active" : `${item.anonymous ? "Voting Ended" : "Poll Ended"}`}
                                    </div>
                                </div>
                                <div className={'poll-heading'}>
                                    <h5>{item.title}</h5>
                                </div>

                                <div className={'poll-description'}>
                                    {item.description}
                                </div>

                                {item.active && (!item.anonymous || !item.hasVoted) &&
                                <div className={'vote-heading'}>
                                    Cast Your Vote-
                                </div>}

                                {(!item.anonymous || !item.hasVoted) && (
                                    <div className={'poll-options-container'}>
                                        <Radio.Group
                                            value={selectedVotes[item.id]}
                                            onChange={(e) =>
                                                setSelectedVotes(prev => ({
                                                    ...prev,
                                                    [item.id]: e.target.value
                                                }))
                                            }
                                            disabled={!item.active}
                                        >
                                            {item.options.map((option: IPollOptions) => (
                                                <div className="poll-options" key={option.id}>
                                                    <Radio value={option.id}>
                                                        {option.text}
                                                    </Radio>
                                                </div>
                                            ))}
                                        </Radio.Group>
                                    </div>
                                )}

                                <div className={'poll-action-button'}>
                                    {item.active && (!item.anonymous || !item.hasVoted) && (
                                        <Button
                                            type="primary"
                                            className="submit-vote-button"
                                            onClick={() => handleVoteSubmit(item.id)}
                                        >
                                            <Check/> {item.anonymous ? "Submit Vote" : "Submit Poll"}
                                        </Button>
                                    )}
                                    {
                                        // item.active &&
                                        item.anonymous &&
                                        item.hasVoted &&
                                        !item.resultDeclared &&
                                        <div className={'result-wait-container'}>
                                            <div className={'lock-icon'}>
                                                <LockKeyhole/>
                                            </div>
                                            <div>
                                                <div className={'title'}>
                                                    Thank you for voting!
                                                </div>
                                                <div className={'sub-title'}>
                                                    Your vote has been submitted successfully. Results will be available
                                                    once the admin declares them.
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {
                                        !item.active &&
                                        item.anonymous &&
                                        !item.hasVoted &&
                                        !item.resultDeclared &&
                                        <div className={'result-wait-container'}>
                                            <div className={'lock-icon'}>
                                                <LockKeyhole/>
                                            </div>
                                            <div>
                                                <div className={'title'}>
                                                    Results will be available soon!
                                                </div>
                                                <div className={'sub-title'}>
                                                    You haven't participated in this vote. Please wait for the admin to
                                                    publish the result.
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {!item.active && item.resultDeclared &&
                                    <Button
                                        type={"primary"}
                                        className={'view-result-btn'}
                                        onClick={() => props.handleViewResult(item.id)}
                                    >
                                        <Eye/> View Result
                                    </Button>
                                    }
                                    {
                                        item.active && !item.anonymous &&
                                        <Button type={"primary"}
                                                className={'view-live-result-btn'}
                                                onClick={() => props.handleOnClick(item.id)}>
                                            <Eye className={'live-result-eye-icon'}/> View Live Results
                                        </Button>
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="poll-empty-container">
                        <EmptyData
                            icon={<ChartColumnBig className={'empty-data-poll-icon'}/>}
                            title="No Polls Available"
                            subtitle={"There are currently no active polls for you to participate in."}
                        />
                    </div>
                )}
            </LoadingSkeleton>
        </>
    )
}

export default PollEmployeeView;
